import React from 'react';
import { ProductCarousel } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import type { CatalogServiceProductData } from 'src/services/catalog/types';
import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { transformProductModelToProductForTracking } from 'src/tracking/common/productTransformer';
import type { TrackingContext } from 'src/types/TrackingContext';
import * as styles from './ProductCarouselSection.style';
import { transformProductModelToProductCarouselItem } from './transformer';

export interface ProductCarouselSectionProps {
  currency: CurrencyCode;
  heading: string;
  lazyImages?: boolean;
  pageType: string;
  products: CatalogServiceProductData[];
  trackingPosition?: number;
  trackingTitle: string;
}

export const ProductCarouselSection = ({
  currency,
  heading,
  lazyImages,
  pageType,
  products,
  trackingPosition,
  trackingTitle,
}: ProductCarouselSectionProps) => {
  const trackingContext: TrackingContext = {
    trackingPosition,
    trackingTitle,
    trackingContentType: 'Product carousel',
  };

  return (
    <SectionContainer columnsXL={10} maxWidth="max" wrapperStyles={styles.wrapper}>
      <ProductCarousel
        heading={heading}
        onNavigate={({ direction, method }) => {
          const trackingDirection = direction === 'next' ? 'next' : 'previous';

          trackCarouselNavigation(method === 'swipe', trackingDirection, {
            ...trackingContext,
            numberOfItems: products.length,
          });
        }}
        /* The payload's own `product` is the OOUX shape the card renders, which does
           not carry what tracking needs (the GBP price, `freeDomesticDelivery`,
           `salePercentage`, the partner shortcode), so the index is used to look the
           catalog product back up. */
        onProductClick={(e, { index }) => {
          if (products.length > 0) {
            trackProductClick(e, {
              ...trackingContext,
              pageType,
              product: transformProductModelToProductForTracking(products[index]),
              productIndex: index,
              numberOfItems: products.length,
            });
          }
        }}
        /* One impression event for the whole carousel, listing every product, rather
           than the per-product `onProductVisible`. Keeps the GA payload identical to
           the Ribbons carousel this replaces. */
        onVisible={() => {
          if (products.length > 0) {
            trackProductImpression({
              ...trackingContext,
              pageType,
              products: products.map(transformProductModelToProductForTracking),
            });
          }
        }}
        products={products.map((product) =>
          transformProductModelToProductCarouselItem(product, {
            currency,
            priority: !lazyImages,
          }),
        )}
      />
    </SectionContainer>
  );
};
